import React from 'react';
import ModeSelectScreen from './ModeSelectScreen';
import ArchetypeGallery from './ArchetypeGallery';
import CustomizeScreen from './CustomizeScreen';
import { ARCHETYPES, findArchetype } from '../templates/archetypes';

// Phase 2 scaffold root. Routes between three views:
//   - mode    — pick Premade / Archetype / From-Scratch
//   - gallery — choose an Archetype (the Tier 2 path; only one wired)
//   - customize — edit the picked archetype, see live preview
//
// Tier 1 (Premade) and Tier 3 (From Scratch) routes are stubbed — clicking
// them logs to console. Phase 5 wires them up.

const CharacterBuilderRoot = () => {
  const [view, setView] = React.useState('mode');
  const [advancedEnabled, setAdvancedEnabled] = React.useState(false);
  const [selectedArchetypeId, setSelectedArchetypeId] = React.useState(null);

  const logSelection = React.useCallback((label, value) => {
    if (typeof window !== 'undefined' && window.console) {
      // eslint-disable-next-line no-console
      window.console.log(`[character-builder] ${label}:`, value);
    }
  }, []);

  const handleSelectMode = React.useCallback(
    (modeId) => {
      logSelection('mode selected', modeId);
      if (modeId === 'archetype') {
        setView('gallery');
      }
      // 'premade' and 'scratch' Phase-5 stubs — stay on mode select for now.
    },
    [logSelection],
  );

  const handleInspect = React.useCallback(
    (archetypeId) => {
      logSelection('inspect archetype', archetypeId);
      // Phase 2: inspect collapses into "select" — we don't yet have a
      // dedicated inspect modal. Tap Select to enter customize.
      setSelectedArchetypeId(archetypeId);
      setView('customize');
    },
    [logSelection],
  );

  const handleSelectArchetype = React.useCallback(
    (archetypeId) => {
      logSelection('select archetype', archetypeId);
      if (archetypeId === '__from-scratch__') {
        // Future Tier-3 entry. Stays on gallery for now.
        return;
      }
      setSelectedArchetypeId(archetypeId);
      setView('customize');
    },
    [logSelection],
  );

  const handleBackToGallery = React.useCallback(() => {
    setView('gallery');
    setSelectedArchetypeId(null);
  }, []);

  const handleBackToMode = React.useCallback(() => {
    setView('mode');
    setSelectedArchetypeId(null);
  }, []);

  const handleComplete = React.useCallback(
    ({ name, archetype }) => {
      logSelection('character complete', { name, archetypeId: archetype?.id });
      // Phase 2 placeholder. Phase 3.5+ wires this to buildCharacter() →
      // useStorage save. For now, log and stay on customize.
    },
    [logSelection],
  );

  const handleAdvanced = React.useCallback(() => {
    logSelection('open advanced', selectedArchetypeId);
    // Phase 5 wires this to the Tier-4 (Advanced) editor.
  }, [logSelection, selectedArchetypeId]);

  if (view === 'gallery') {
    return (
      <ArchetypeGallery
        archetypes={ARCHETYPES}
        onInspect={handleInspect}
        onSelect={handleSelectArchetype}
        onBack={handleBackToMode}
      />
    );
  }

  if (view === 'customize') {
    return (
      <CustomizeScreen
        archetype={findArchetype(selectedArchetypeId)}
        onBack={handleBackToGallery}
        onComplete={handleComplete}
        onAdvanced={handleAdvanced}
      />
    );
  }

  return (
    <ModeSelectScreen
      onSelectMode={handleSelectMode}
      advancedEnabled={advancedEnabled}
      onAdvancedToggle={setAdvancedEnabled}
    />
  );
};

export default CharacterBuilderRoot;
